import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, fmtISO, fmtLong, isSameDay } from '../lib/format.js';
import { LOG_WINDOW_DAYS } from '../lib/reviewWindow.js';
import { DateBar } from './DateBar.jsx';
import { EntryRow } from './EntryRow.jsx';
import { ExcludedBadge } from './ExcludedBadge.jsx';
import { Icon } from './Icon.jsx';
import { LockedRibbon } from './LockedRibbon.jsx';
import { PrinciplesCard } from './Principles.jsx';
import { ProgressMeter } from './ProgressMeter.jsx';

/**
 * Single "Import ▾" button that opens a small menu with the available
 * sources. Calendar and/or Jira items are conditionally rendered — when
 * either is missing the surviving entry still works. If both are gone
 * the menu doesn't render at all (the parent skips the wrapper).
 */
function ImportMenu({ onImportCalendar, onImportJira, onImportCopyDay }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-secondary btn-lg"
        onClick={() => setOpen((o) => !o)}
        title="Pre-fill entries from another source"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Import <Icon name="chevD" size={12} />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 30,
            minWidth: 240,
            padding: 6,
            background: '#fff',
            border: '1px solid var(--sp-border)',
            borderRadius: 12,
            boxShadow: 'var(--sp-shadow-md)',
          }}
        >
          {onImportJira && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onImportJira();
              }}
              className="tt-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 12px',
                border: 0,
                background: 'transparent',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: '#E9F2FF',
                  color: '#0052CC',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                <Icon name="jiraMark" size={14} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span
                  style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)' }}
                >
                  From Jira
                </span>
                <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>
                  Tickets you touched today
                </span>
              </span>
            </button>
          )}
          {onImportCalendar && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onImportCalendar();
              }}
              className="tt-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 12px',
                border: 0,
                background: 'transparent',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: 'var(--sp-mint-100)',
                  color: '#007C7C',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                <Icon name="cal" size={14} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
                  From Calendar
                </span>
                <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>
                  Today's meetings & events
                </span>
              </span>
            </button>
          )}
          {onImportCopyDay && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onImportCopyDay();
              }}
              className="tt-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 12px',
                border: 0,
                background: 'transparent',
                borderRadius: 8,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: '#FFF1E1',
                  color: '#B57651',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                <Icon name="repeat" size={14} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sp-fg-1)' }}>
                  From last week
                </span>
                <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>
                  Cherry-pick a repeating day
                </span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// v2.0 (A3) — holiday empty state. Replaces the usual "Nothing logged" card
// when the selected date is a public holiday for the user's team. No
// "Log time" CTA: the day is non-working and doesn't count toward the
// monthly target.
const FLAG_EMOJI = { PH: '🇵🇭', PL: '🇵🇱' };
function HolidayEmptyState({ holiday, country, dateLabel }) {
  return (
    <div
      className="card"
      style={{
        padding: '40px 32px',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 22,
        background: 'linear-gradient(180deg, #fff 0%, #FAFAFB 100%)',
        border: '1px solid var(--sp-border)',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: 'var(--sp-bg-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          fontSize: 28,
          lineHeight: 1,
        }}
      >
        <span aria-hidden="true">{FLAG_EMOJI[country] || '🏖️'}</span>
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
          {FLAG_EMOJI[country]} {holiday.name} — non-working
        </h3>
        <p
          style={{
            margin: '4px 0 0',
            color: 'var(--sp-fg-2)',
            fontSize: 14,
          }}
        >
          {dateLabel} is a public holiday
          {country === 'PH'
            ? ' in the Philippines'
            : country === 'PL'
            ? ' in Poland'
            : ''}
          . No time is expected today.
        </p>
      </div>
    </div>
  );
}

// `canLog` is false on read-only days (a locked month, an approver drill-in,
// or a day older than the 35-day logging window). We then drop the "Log time"
// CTA and the backfill nudge — the banner/ribbon above already explains why —
// so the empty state doesn't promise an action the day can't take.
function EmptyState({ onLog, isToday, canLog = true }) {
  return (
    <div
      className="card"
      style={{
        padding: '40px 32px',
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        background: 'linear-gradient(180deg, #fff 0%, #FAFFFE 100%)',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: 'var(--sp-mint-100)',
          color: '#007C7C',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <Icon name="clock" size={26} stroke={1.8} />
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700 }}>
          {isToday ? 'Nothing logged yet today' : 'Nothing logged on this day'}
        </h3>
        {canLog && (
          <p
            style={{
              margin: '4px 0 0',
              color: 'var(--sp-fg-2)',
              fontSize: 14,
            }}
          >
            {isToday
              ? 'Start with your first entry — it takes about thirty seconds.'
              : `You can still backfill entries up to ${LOG_WINDOW_DAYS} days back.`}
          </p>
        )}
      </div>
      {canLog && (
        <button className="btn btn-primary" onClick={onLog}>
          <Icon name="plus" size={16} stroke={2.4} /> Log time
        </button>
      )}
    </div>
  );
}

export function TodayView({
  entries,
  selectedDate,
  onDateChange,
  onLog,
  onEdit,
  onDelete,
  user,
  dailyHoursTarget,
  hideWeekends,
  onToggleHideWeekends,
  loading = false,
  summaryByDate = {},
  // v2.6 — review window: the date bar shows `viewDays` days and pages back
  // via `onLoadOlder` (gated by `canLoadOlder`). `beyondLogWindow` is true
  // when the selected day is older than the 35-day logging window, which puts
  // the whole day into review-only mode (no logging/edit/delete).
  viewDays = LOG_WINDOW_DAYS,
  onLoadOlder,
  canLoadOlder = false,
  beyondLogWindow = false,
  onImportCalendar,
  onImportJira,
  onImportCopyDay,
  showPrinciplesCard = false,
  onDismissPrinciplesCard,
  onOpenPrinciples,
  holidays = { country: '', byDate: new Map() },
  lockedMonths = [],
  readOnlySubject = null,
  onView,
  // v2.6 — excluded user ("not required to log time"): shows an
  // optional-logging banner, suppresses the principles onboarding card,
  // and the day meter drops its deficit framing. Logging stays usable
  // (voluntary). True for the excluded user's own Today and for an
  // approver drilling into one (where the read-only banner also applies).
  exempt = false,
}) {
  const holiday = holidays?.byDate?.get
    ? holidays.byDate.get(fmtISO(selectedDate))
    : null;
  // v2.0 (A6) — is the selected day inside an approved month for this user?
  const selectedMonthKey = fmtISO(selectedDate).slice(0, 7);
  const monthLocked = lockedMonths.includes(selectedMonthKey);
  // Drill-in (approver viewing a report) is also a kind of read-only mode,
  // but driven by `readOnlySubject` not the lock list. Either gate hides
  // the write affordances.
  // v2.6 — a day older than the 35-day logging window is review-only too:
  // entries are viewable but not editable, and there's no "Log time" CTA
  // (logging stays capped at 35 days). Locked-month and drill-in have their
  // own banners, so `reviewOnly` is scoped to *just* the beyond-window case.
  const reviewOnly = beyondLogWindow && !monthLocked && !readOnlySubject;
  const readOnly = !!readOnlySubject || monthLocked || beyondLogWindow;
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const isToday = isSameDay(selectedDate, today);
  const targetMin = dailyHoursTarget * 60;
  const dayEntries = entries
    .filter((e) => !e.deleted)
    .sort((a, b) =>
      (a.createdAt || '').localeCompare(b.createdAt || ''),
    );
  const totalMins = dayEntries.reduce(
    (s, e) => s + e.hours * 60 + e.minutes,
    0,
  );
  const firstName = user?.name?.split(' ')?.[0];

  // Hide non-working days is misleading when the active day IS a weekend —
  // hiding it would remove the very day the user is looking at. Force-off
  // and disable the toggle in that case; the user's preference is preserved
  // and re-applies as soon as they navigate back to a weekday.
  const selectedIsWeekend =
    selectedDate.getDay() === 0 || selectedDate.getDay() === 6;
  const effectiveHideWeekends = hideWeekends && !selectedIsWeekend;

  return (
    <div className="tt-page">
      {/* v2.0 (A6) — locked-month ribbon. Renders before the page body so
          it visually sits under the top bar regardless of scroll. Only
          shown to the locked-out user themselves; approvers in drill-in
          mode get the read-only banner instead, which already explains. */}
      {monthLocked && !readOnlySubject && (
        <LockedRibbon
          month={selectedMonthKey}
          approvedBy=""
          approvedAt=""
        />
      )}
      {/* v2.0 (A4 drill-in) — approver-viewing-report banner. A compact
          apricot pill that hugs its content and sits above the heading,
          marking the whole screen as a read-only view of the report's
          month. */}
      {readOnlySubject && (
        <div
          style={{
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              background: '#FFF7E6',
              color: '#A05A00',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="lock" size={12} /> Read-only — viewing{' '}
            {readOnlySubject.name || readOnlySubject.email}'s month
          </div>
          {/* v2.6 — mark a drilled-into excluded report. */}
          {exempt && <ExcludedBadge size="sm" />}
        </div>
      )}
      {/* v2.6 — review-only banner for a day older than the 35-day logging
          window. The entries are here to review; logging is only available
          for the last 35 days. Apricot to echo the locked/drill-in read-only
          treatment. */}
      {reviewOnly && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              background: '#FFF7E6',
              color: '#A05A00',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="lock" size={12} /> Review only — you can log time only
            for the last {LOG_WINDOW_DAYS} days. Older entries are here to look
            back on.
          </div>
        </div>
      )}
      {/* v2.6 — optional-logging banner on the excluded user's OWN Today.
          No deficit framing, no reminders; logging stays voluntary. */}
      {exempt && !readOnlySubject && (
        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              background: 'var(--sp-mint-100)',
              color: '#007C7C',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="clock" size={12} /> Logging is optional for you —
            you're not required to track time, and you won't get reminders.
          </div>
        </div>
      )}
      {/* v2.0 (A1) — first-session onboarding card. Sits above the date
          eyebrow so it's the first thing the user sees, and dismissing it
          collapses cleanly into the existing header. Suppressed in
          drill-in to avoid the card following an approver around. */}
      {/* v2.6 — also suppressed for excluded users: the daily-logging
          principles don't apply to someone not required to log. */}
      {!readOnlySubject &&
        !exempt &&
        showPrinciplesCard &&
        onDismissPrinciplesCard && (
          <PrinciplesCard
            onDismiss={onDismissPrinciplesCard}
            onLearnMore={() => {
              onOpenPrinciples?.();
              onDismissPrinciplesCard();
            }}
          />
        )}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
              marginBottom: 4,
            }}
          >
            {isToday
              ? 'Today'
              : isSameDay(selectedDate, addDays(today, -1))
              ? 'Yesterday'
              : 'Selected day'}
          </div>
          <h1
            style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.01em' }}
          >
            {fmtLong(selectedDate)}
          </h1>
        </div>
        <div style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
          {/* Imports + Log time are gone in any read-only state — drill-in
              or locked-month. The disabled-button pattern would be noisy. */}
          {!readOnly && (onImportCalendar || onImportJira || onImportCopyDay) && (
            <ImportMenu
              onImportCalendar={onImportCalendar}
              onImportJira={onImportJira}
              onImportCopyDay={onImportCopyDay}
            />
          )}
          {!readOnly ? (
            <button
              className="btn btn-primary btn-lg"
              onClick={() => onLog(null)}
            >
              <Icon name="plus" size={16} stroke={2.4} /> Log time
            </button>
          ) : monthLocked && !readOnlySubject ? (
            // Locked-month for self: keep the disabled affordance visible
            // so the user understands why they can't add — and the tooltip
            // points at the right next step.
            <button
              className="btn btn-primary btn-lg"
              disabled
              title="This month is approved and locked — ask your manager to reopen it"
              style={{ opacity: 0.4, cursor: 'not-allowed' }}
            >
              <Icon name="lock" size={14} stroke={2.4} /> Log time
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
            }}
          >
            {viewDays <= LOG_WINDOW_DAYS
              ? `Past ${LOG_WINDOW_DAYS} days`
              : `Past ${viewDays} days`}
          </span>
          <button
            type="button"
            className="tt-toggle"
            aria-pressed={!!effectiveHideWeekends}
            disabled={selectedIsWeekend}
            onClick={() => {
              if (selectedIsWeekend) return;
              onToggleHideWeekends?.(!hideWeekends);
            }}
            title={
              selectedIsWeekend
                ? 'Disabled while viewing a weekend day'
                : 'Toggle non-working days'
            }
            style={
              selectedIsWeekend
                ? { opacity: 0.45, cursor: 'not-allowed' }
                : null
            }
          >
            <span className="tt-toggle-track">
              <span className="tt-toggle-thumb" />
            </span>
            Hide non-working days
          </button>
        </div>
        <DateBar
          selected={selectedDate}
          onSelect={onDateChange}
          hideWeekends={effectiveHideWeekends}
          selectedDayMins={totalMins}
          summaryByDate={summaryByDate}
          holidayByDate={holidays.byDate}
          holidayCountry={holidays.country}
          viewDays={viewDays}
          onLoadOlder={onLoadOlder}
          canLoadOlder={canLoadOlder}
        />
      </div>

      {loading ? (
        <div
          className="card"
          style={{
            padding: 20,
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div className="tt-skel" style={{ width: 140, height: 38 }} />
            <div className="tt-skel" style={{ width: 80, height: 14 }} />
            <div style={{ flex: 1 }} />
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                color: 'var(--sp-fg-3)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <span className="tt-spinner sm" /> Calculating total…
            </div>
          </div>
          <div
            className="tt-skel"
            style={{ width: '100%', height: 18, borderRadius: 6 }}
          />
        </div>
      ) : (
        <ProgressMeter
          entries={dayEntries}
          target={targetMin}
          userFirstName={firstName}
          exempt={exempt}
        />
      )}

      <div style={{ marginTop: 28 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 10,
            padding: '0 4px',
          }}
        >
          <h2
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--sp-fg-2)',
              letterSpacing: '0.02em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {loading ? (
              <>
                <span
                  className="tt-spinner sm"
                  style={{ color: 'var(--sp-fg-3)' }}
                />
                <span style={{ color: 'var(--sp-fg-3)' }}>
                  Refreshing entries…
                </span>
              </>
            ) : (
              <>
                {dayEntries.length}{' '}
                {dayEntries.length === 1 ? 'entry' : 'entries'}
              </>
            )}
          </h2>
          <span
            style={{ flex: 1, height: 1, background: 'var(--sp-border)' }}
          />
        </div>

        {loading ? (
          <div className="card" style={{ padding: 8, borderRadius: 16 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 12px',
                  borderBottom:
                    i < 2 ? '1px solid var(--sp-border-soft)' : 0,
                }}
              >
                <div
                  className="tt-skel"
                  style={{ width: 64, height: 22 }}
                />
                <div
                  className="tt-skel"
                  style={{ width: 140, height: 14 }}
                />
                <div
                  className="tt-skel"
                  style={{ width: 80, height: 14 }}
                />
                <div style={{ flex: 1 }} />
                <div
                  className="tt-skel"
                  style={{ width: 56, height: 14 }}
                />
              </div>
            ))}
          </div>
        ) : dayEntries.length === 0 ? (
          // v2.0 (A3) — holiday days get a non-working-day empty state
          // instead of the usual "Log time" nudge. The CTA is intentionally
          // absent: it's a public holiday for the user's team.
          holiday ? (
            <HolidayEmptyState
              holiday={holiday}
              country={holidays.country}
              dateLabel={fmtLong(selectedDate)}
            />
          ) : (
            <EmptyState
              onLog={() => onLog(null)}
              isToday={isToday}
              canLog={!readOnly}
            />
          )
        ) : (
          <div className="card" style={{ padding: 8, borderRadius: 16 }}>
            {dayEntries.map((e, i) => (
              <React.Fragment key={e.id}>
                {i > 0 && (
                  <hr className="tt-divider" style={{ margin: '0 12px' }} />
                )}
                <EntryRow
                  entry={e}
                  onEdit={readOnly ? undefined : onEdit}
                  onDelete={readOnly ? undefined : onDelete}
                  onView={readOnly ? onView : undefined}
                  locked={readOnly}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
